import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  useTheme,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Event,
  LocationOn,
  Visibility,
  Edit,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  parseISO,
  isSameMonth,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isWithinInterval,
  startOfDay,
  endOfDay,
  addDays,
} from 'date-fns';
import { getCalendarUserColor } from '../../utils/calendarUserColors';

interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  eventType: 'client_meeting' | 'court_hearing' | 'deposition' | 'mediation' | 'consultation' | 'filing_deadline' | 'document_review' | 'billing_review' | 'staff_meeting' | 'training' | 'other';
  startDate: string;
  endDate: string;
  allDay: boolean;
  location: string | null;
  status: 'scheduled' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  reminderEnabled: boolean;
  reminderTime: number;
  reminderSent: boolean;
  notes: string | null;
  attendees: any[];
  recurrence: any;
  case: { id: string; caseNumber: string; title: string; } | null;
  client: { id: string; name: string; } | null;
  task: { id: string; title: string; status: string; } | null;
  creator: { id: string; name: string; email: string; } | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface MiniCalendarProps {
  events: CalendarEvent[];
  currentDate: Date;
  onEventClick: (event: CalendarEvent) => void;
  onEventEdit: (event: CalendarEvent) => void;
  onEventView: (event: CalendarEvent) => void;
  onDateChange: (date: Date) => void;
}

const MiniCalendar: React.FC<MiniCalendarProps> = ({
  events,
  currentDate,
  onEventClick,
  onEventEdit,
  onEventView,
  onDateChange,
}) => {
  const theme = useTheme();
  const [hoveredEvent, setHoveredEvent] = useState<string | null>(null);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getEventColor = (event: CalendarEvent) =>
    getCalendarUserColor(event.creator, theme.palette.grey[500]);

  const getEventsForDay = (date: Date) => {
    return events.filter(event => {
      const eventStart = parseISO(event.startDate);
      const eventEnd = parseISO(event.endDate);
      const dayStart = startOfDay(date);
      const dayEnd = endOfDay(date);
      
      return isWithinInterval(eventStart, { start: dayStart, end: dayEnd }) ||
             isWithinInterval(eventEnd, { start: dayStart, end: dayEnd }) ||
             (eventStart <= dayStart && eventEnd >= dayEnd);
    });
  };

  const getUpcomingEvents = () => {
    const now = new Date();
    const nextWeek = addDays(now, 7);
    
    return events
      .filter(event => {
        const eventStart = parseISO(event.startDate);
        return eventStart > now && eventStart <= nextWeek;
      })
      .sort((a, b) => parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime())
      .slice(0, 5);
  };

  const handleEventClick = (event: CalendarEvent) => {
    console.log('Event clicked:', event);
    // Always open the event details dialog instead of navigating away
    onEventClick(event);
  };

  const handlePreviousMonth = () => {
    onDateChange(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    onDateChange(addMonths(currentDate, 1));
  };

  const weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  return (
    <Paper sx={{ p: 2, maxWidth: 300 }}>
      {/* Month Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <IconButton size="small" onClick={handlePreviousMonth}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="h6" fontWeight="bold">
          {format(currentDate, 'MMM yyyy')}
        </Typography>
        <IconButton size="small" onClick={handleNextMonth}>
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Week day headers */}
      <Grid container sx={{ mb: 1 }}>
        {weekDays.map((day, index) => (
          <Grid item xs key={index}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {day}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Calendar days */}
      <Grid container spacing={0.5}>
        {calendarDays.map((day, index) => {
          const dayEvents = getEventsForDay(day);
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isToday = isSameDay(day, new Date());

          return (
            <Grid item xs key={index}>
              <Box
                sx={{
                  aspectRatio: '1',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isToday 
                    ? theme.palette.primary.main 
                    : isCurrentMonth 
                      ? 'transparent' 
                      : theme.palette.action.hover,
                  color: isToday 
                    ? theme.palette.primary.contrastText 
                    : isCurrentMonth 
                      ? 'text.primary' 
                      : 'text.disabled',
                  borderRadius: 1,
                  position: 'relative',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                  minHeight: 32,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: isToday ? 'bold' : 'normal',
                    fontSize: '0.75rem',
                  }}
                >
                  {format(day, 'd')}
                </Typography>

                {/* Event indicators */}
                {dayEvents.length > 0 && (
                  <Box sx={{ position: 'absolute', bottom: 1 }}>
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: getEventColor(dayEvents[0]),
                      }}
                    />
                  </Box>
                )}

                {/* Event tooltip */}
                {dayEvents.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 1000,
                      display: 'none',
                      '&:hover': {
                        display: 'block',
                      },
                    }}
                  >
                    <Card sx={{ minWidth: 200, maxWidth: 300 }}>
                      <CardContent sx={{ p: 1 }}>
                        {dayEvents.map((event) => (
                          <Box
                            key={event.id}
                            sx={{
                              p: 0.5,
                              mb: 0.5,
                              backgroundColor: getEventColor(event),
                              color: 'white',
                              borderRadius: 1,
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                            onClick={() => handleEventClick(event)}
                            onMouseEnter={() => setHoveredEvent(event.id)}
                            onMouseLeave={() => setHoveredEvent(null)}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                              {event.title}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                              {format(parseISO(event.startDate), 'HH:mm')}
                            </Typography>
                            {event.case && (
                              <Chip
                                label={event.case.caseNumber}
                                size="small"
                                sx={{
                                  mt: 0.5,
                                  backgroundColor: 'rgba(255,255,255,0.2)',
                                  color: 'white',
                                  fontSize: '0.5rem',
                                  height: 12,
                                }}
                              />
                            )}
                            
                            {hoveredEvent === event.id && (
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                <Tooltip title="View">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onEventView(event);
                                    }}
                                    sx={{ color: 'white', p: 0.5 }}
                                  >
                                    <Visibility sx={{ fontSize: '0.6rem' }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onEventEdit(event);
                                    }}
                                    sx={{ color: 'white', p: 0.5 }}
                                  >
                                    <Edit sx={{ fontSize: '0.6rem' }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Events Summary */}
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={1}>
          {/* Today's Events */}
          <Grid item xs={6}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Today's Events
            </Typography>
            {getEventsForDay(new Date()).slice(0, 2).map((event) => (
              <Box
                key={event.id}
                sx={{
                  p: 0.5,
                  mb: 0.5,
                  backgroundColor: getEventColor(event),
                  color: 'white',
                  borderRadius: 1,
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
                onClick={() => handleEventClick(event)}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', fontSize: '0.65rem' }}>
                  {event.title}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                  {format(parseISO(event.startDate), 'HH:mm')}
                </Typography>
              </Box>
            ))}
            {getEventsForDay(new Date()).length === 0 && (
              <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                No events today
              </Typography>
            )}
          </Grid>
          
          {/* Upcoming Events */}
          <Grid item xs={6}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Upcoming Events
            </Typography>
            {getUpcomingEvents().slice(0, 2).map((event) => (
              <Box
                key={event.id}
                sx={{
                  p: 0.5,
                  mb: 0.5,
                  backgroundColor: getEventColor(event),
                  color: 'white',
                  borderRadius: 1,
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
                onClick={() => handleEventClick(event)}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', fontSize: '0.65rem' }}>
                  {event.title}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                  {format(parseISO(event.startDate), 'MMM dd')}
                </Typography>
              </Box>
            ))}
            {getUpcomingEvents().length === 0 && (
              <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                No upcoming events
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default MiniCalendar; 